// SiteLayoutEditor — Floor Stack Phase D.
//
// Per-site, per-floor layout editor: pick a building & floor, upload
// SVG/PNG/PDF source, place marker overlay, save and publish.
//
// Renders an SVG canvas (1000x700 viewBox) with the uploaded image as
// the background. Click empty space → place marker (modal for type +
// label). Click existing marker → select; arrow keys / drag move it.
// "Save & Publish" pushes a publish-version request, then re-fetches.
//
// This is the production scaffold for Phase D — Phase E plugs the same
// data into the diamond-stack visual.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Building2, FilePlus2, MapPin, RefreshCw, Save, ServerCrash, Trash2 } from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import Skeleton from "../components/Skeleton.js";
import { Field } from "../components/Field.js";
import {
  archiveMarker,
  createBuilding,
  createFloor,
  createMarker,
  EMPTY_LAYOUT,
  friendlyFloorStackError,
  getFloorLayout,
  getFloorStack,
  listMarkers,
  patchMarker,
  publishLayoutVersion,
  type BuildingDto,
  type BuildingFloorDto,
  type FloorStackResponse,
  type MapLayoutVersionDto,
  type MapMarkerDto,
  type MarkerType,
  type NormalizedLayoutDto,
} from "../lib/floorStackApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import LayoutGeometry from "../floor-stack/LayoutGeometry.js";

const MARKER_TYPE_OPTIONS: Array<{ value: MarkerType; label: string }> = [
  { value: "location", label: "地點" },
  { value: "route_node", label: "路線節點" },
  { value: "incident_pin", label: "事件 Pin" },
  { value: "note", label: "備註" },
  { value: "camera", label: "監視器" },
  { value: "aed", label: "AED" },
  { value: "exit", label: "逃生出口" },
];

const MARKER_FILL: Record<MarkerType, string> = {
  location: "#3ec1ff",
  route_node: "#ffa743",
  incident_pin: "#ff5b6d",
  note: "#a3a8b3",
  camera: "#65f088",
  aed: "#ff5b6d",
  exit: "#65f088",
};

const VIEWBOX_W = 1000;
const VIEWBOX_H = 700;

type StackState =
  | { status: "loading" }
  | { status: "ready"; data: FloorStackResponse }
  | { status: "error"; message: string };

type FloorState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; layoutId: string | null; version: MapLayoutVersionDto | null; markers: MapMarkerDto[] }
  | { status: "error"; message: string };

interface AddMarkerModalState {
  x: number;
  y: number;
}

export default function SiteLayoutEditor(): React.ReactElement {
  const { siteId = "" } = useParams<{ siteId: string }>();
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite = session.scopes.includes("write:site") || session.scopes.includes("admin:tenant") || session.super_admin === true;

  const [stack, setStack] = useState<StackState>({ status: "loading" });
  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);
  const [selectedFloor, setSelectedFloor] = useState<string | null>(null);
  const [floor, setFloor] = useState<FloorState>({ status: "idle" });
  const [busy, setBusy] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [addMarker, setAddMarker] = useState<AddMarkerModalState | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const [bootstrap, setBootstrap] = useState<{ kind: "building" | "floor" } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const loadStack = useCallback(async () => {
    if (!siteId) return;
    setStack({ status: "loading" });
    try {
      const data = await getFloorStack(siteId, tenantParam);
      setStack({ status: "ready", data });
      // Auto-select first building / floor
      const firstBuilding = data.buildings[0];
      if (firstBuilding) {
        setSelectedBuilding(firstBuilding.id);
        const firstFloor = firstBuilding.floors.find((f) => f.status !== "archived");
        setSelectedFloor(firstFloor?.id ?? null);
      } else {
        setSelectedBuilding(null);
        setSelectedFloor(null);
      }
    } catch (err) {
      setStack({ status: "error", message: friendlyFloorStackError(err) });
    }
  }, [siteId, tenantParam]);

  useEffect(() => { void loadStack(); }, [loadStack]);

  const loadFloor = useCallback(async (floorId: string) => {
    setFloor({ status: "loading" });
    try {
      const layoutResp = await getFloorLayout(floorId, tenantParam);
      if (!layoutResp) {
        setFloor({ status: "loaded", layoutId: null, version: null, markers: [] });
        return;
      }
      const ver = layoutResp.current_version;
      const markers = ver
        ? await listMarkers(layoutResp.layout.id, ver.id, tenantParam)
        : [];
      setFloor({ status: "loaded", layoutId: layoutResp.layout.id, version: ver, markers });
    } catch (err) {
      setFloor({ status: "error", message: friendlyFloorStackError(err) });
    }
  }, [tenantParam]);

  useEffect(() => {
    if (selectedFloor) void loadFloor(selectedFloor);
    else setFloor({ status: "idle" });
  }, [selectedFloor, loadFloor]);

  const buildings = stack.status === "ready" ? stack.data.buildings : [];
  const buildingObj: (BuildingDto & { floors: Array<BuildingFloorDto & unknown> }) | undefined = useMemo(
    () => buildings.find((b) => b.id === selectedBuilding),
    [buildings, selectedBuilding],
  );
  const floors: BuildingFloorDto[] = buildingObj?.floors.filter((f) => f.status !== "archived") ?? [];

  // Bootstrap a building when a brand-new site has none.
  const handleCreateBuilding = useCallback(async (name: string) => {
    setBusy("create_building");
    setErrorBanner(null);
    try {
      const r = await createBuilding({ site_id: siteId, name, set_as_default: true }, tenantParam);
      await loadStack();
      setSelectedBuilding(r.id);
    } catch (err) {
      setErrorBanner(friendlyFloorStackError(err));
    } finally {
      setBusy(null);
      setBootstrap(null);
    }
  }, [siteId, tenantParam, loadStack]);

  const handleCreateFloor = useCallback(async (input: { floor_no: number; floor_name: string }) => {
    if (!selectedBuilding) return;
    setBusy("create_floor");
    setErrorBanner(null);
    try {
      const r = await createFloor({
        building_id: selectedBuilding,
        floor_no: input.floor_no,
        floor_name: input.floor_name,
        display_order: input.floor_no,
      }, tenantParam);
      await loadStack();
      setSelectedFloor(r.id);
    } catch (err) {
      setErrorBanner(friendlyFloorStackError(err));
    } finally {
      setBusy(null);
      setBootstrap(null);
    }
  }, [selectedBuilding, tenantParam, loadStack]);

  // Note: layout upload + AI parse + geometry edit all happen on the
  // dedicated FloorLayoutEditor at
  // /dashboard/sites/:siteId/buildings/:bid/floors/:fid/layout.
  // This page (SiteLayoutEditor) is for marker placement only — every
  // "change base map" action is a Link out to that page so the operator
  // sees the upload progress + ChatGPT parse + edit tools in one place.
  const floorEditorHref = (selectedBuilding && selectedFloor)
    ? `/dashboard/sites/${siteId}/buildings/${selectedBuilding}/floors/${selectedFloor}/layout`
    : null;

  const handlePublish = useCallback(async () => {
    if (floor.status !== "loaded" || !floor.layoutId || !floor.version) return;
    setBusy("publish");
    setErrorBanner(null);
    try {
      await publishLayoutVersion(floor.layoutId, floor.version.id, tenantParam);
      if (selectedFloor) await loadFloor(selectedFloor);
    } catch (err) {
      setErrorBanner(friendlyFloorStackError(err));
    } finally {
      setBusy(null);
    }
  }, [floor, tenantParam, selectedFloor, loadFloor]);

  // ── Canvas interactions ─────────────────────────────────────────────────

  const eventToViewbox = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width * VIEWBOX_W;
    const py = (e.clientY - rect.top) / rect.height * VIEWBOX_H;
    return { x: Math.max(0, Math.min(VIEWBOX_W, px)), y: Math.max(0, Math.min(VIEWBOX_H, py)) };
  }, []);

  const onCanvasClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!canWrite) return;
    if (floor.status !== "loaded" || !floor.layoutId || !floor.version) {
      setErrorBanner("請先上傳圖資再標注。");
      return;
    }
    const tgt = e.target as Element;
    if (tgt && tgt.closest("[data-marker-id]")) return; // clicked an existing marker
    const pos = eventToViewbox(e);
    if (pos) setAddMarker(pos);
  }, [canWrite, floor, eventToViewbox]);

  const handleAddMarker = useCallback(async (kind: MarkerType, label: string) => {
    if (!addMarker || floor.status !== "loaded" || !floor.layoutId || !floor.version) return;
    setBusy("create_marker");
    setErrorBanner(null);
    try {
      await createMarker(floor.layoutId, floor.version.id, {
        marker_type: kind,
        position_x: addMarker.x,
        position_y: addMarker.y,
        label: label || undefined,
      }, tenantParam);
      if (selectedFloor) await loadFloor(selectedFloor);
    } catch (err) {
      setErrorBanner(friendlyFloorStackError(err));
    } finally {
      setBusy(null);
      setAddMarker(null);
    }
  }, [addMarker, floor, tenantParam, selectedFloor, loadFloor]);

  const handleMarkerArchive = useCallback(async (markerId: string) => {
    setBusy("archive_marker");
    setErrorBanner(null);
    try {
      await archiveMarker(markerId, tenantParam);
      if (selectedFloor) await loadFloor(selectedFloor);
      setSelectedMarker(null);
    } catch (err) {
      setErrorBanner(friendlyFloorStackError(err));
    } finally {
      setBusy(null);
    }
  }, [tenantParam, selectedFloor, loadFloor]);

  const handleMarkerDrag = useCallback(async (markerId: string, x: number, y: number) => {
    try {
      await patchMarker(markerId, { position_x: x, position_y: y }, tenantParam);
      if (selectedFloor) await loadFloor(selectedFloor);
    } catch (err) {
      setErrorBanner(friendlyFloorStackError(err));
    }
  }, [tenantParam, selectedFloor, loadFloor]);

  // ── Render ───────────────────────────────────────────────────────────

  if (stack.status === "loading") {
    return (
      <div>
        <PageHeader title="樓層圖資編輯" description="載入中…" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (stack.status === "error") {
    return (
      <div>
        <PageHeader title="樓層圖資編輯" />
        <EmptyState
          icon={ServerCrash}
          title="無法載入"
          description={stack.message}
          action={<button onClick={loadStack} className="btn">重試</button>}
        />
      </div>
    );
  }
  const data = stack.data;

  return (
    <div data-testid="layout-editor">
      <PageHeader
        title={`樓層圖資編輯 — ${data.site.name}`}
        description="上傳 SVG / PNG / PDF 底圖，點擊空白處新增標記點，發佈版本後會即時推到樓層檢視。"
        actions={
          <Link to={`/dashboard/sites`} className="btn">回案場列表</Link>
        }
      />

      {errorBanner && (
        <div className="mb-4 rounded border border-red-500/50 bg-red-500/10 px-3 py-2 text-sm text-red-300">{errorBanner}</div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_1fr]">
        {/* ── Sidebar: building + floor picker ──────────────────────────── */}
        <aside className="rounded border border-border bg-bg-surface p-3">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-text-primary">建築</h2>
            {canWrite && (
              <button
                onClick={() => setBootstrap({ kind: "building" })}
                className="text-xs text-text-secondary hover:text-text-primary"
              >
                + 新增
              </button>
            )}
          </div>
          {buildings.length === 0 ? (
            <p className="text-xs text-text-secondary">尚未建立任何建築。</p>
          ) : (
            <ul className="mb-4 space-y-1">
              {buildings.map((b) => (
                <li key={b.id}>
                  <button
                    onClick={() => { setSelectedBuilding(b.id); setSelectedFloor(b.floors[0]?.id ?? null); }}
                    data-active={selectedBuilding === b.id}
                    className={`w-full rounded px-2 py-1.5 text-left text-sm flex items-center gap-2 ${selectedBuilding === b.id ? "bg-bg-raised text-text-primary" : "text-text-secondary hover:bg-bg-hover"}`}
                  >
                    <Building2 className="h-3.5 w-3.5" />
                    <span className="truncate">{b.name}</span>
                    <span className="ml-auto text-xs text-text-tertiary">{b.floors_count}F</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-text-primary">樓層</h2>
            {canWrite && selectedBuilding && (
              <button
                onClick={() => setBootstrap({ kind: "floor" })}
                className="text-xs text-text-secondary hover:text-text-primary"
              >
                + 新增
              </button>
            )}
          </div>
          {floors.length === 0 ? (
            <p className="text-xs text-text-secondary">這棟還沒有樓層。</p>
          ) : (
            <ul className="space-y-1">
              {floors.map((f) => (
                <li key={f.id}>
                  <button
                    onClick={() => setSelectedFloor(f.id)}
                    className={`w-full rounded px-2 py-1.5 text-left text-sm flex items-center gap-2 ${selectedFloor === f.id ? "bg-bg-raised text-text-primary" : "text-text-secondary hover:bg-bg-hover"}`}
                  >
                    <span className="font-mono text-xs">{f.floor_name}</span>
                    {f.layout_id ? (
                      <span className="ml-auto text-xs text-emerald-400">已上傳</span>
                    ) : (
                      <span className="ml-auto text-xs text-text-tertiary">無圖資</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* ── Canvas area ──────────────────────────────────────────────── */}
        <section className="rounded border border-border bg-bg-surface p-3">
          {!selectedFloor ? (
            <EmptyState
              icon={MapPin}
              title="先選一個樓層"
              description="左側選一棟建築與樓層後，這裡會顯示圖資編輯器。"
            />
          ) : floor.status === "loading" ? (
            <Skeleton className="aspect-[10/7] w-full" />
          ) : floor.status === "error" ? (
            <EmptyState icon={ServerCrash} title="樓層讀取失敗" description={floor.message} />
          ) : floor.status === "loaded" && !floor.layoutId ? (
            <div className="flex flex-col items-center justify-center rounded border border-dashed border-border bg-bg-base px-6 py-12 text-center">
              <FilePlus2 className="mb-3 h-10 w-10 text-text-secondary" />
              <h3 className="mb-1 text-base font-semibold text-text-primary">這層樓尚未有底圖</h3>
              <p className="mb-4 max-w-md text-sm text-text-secondary">底圖上傳、AI 解析（牆 / 房間 / 門 / 走道）與幾何編輯都在「底圖編輯」頁完成。完成後再回這頁放 marker。</p>
              {canWrite && floorEditorHref ? (
                <Link to={floorEditorHref} className="btn-primary inline-flex items-center gap-2">
                  <FilePlus2 className="h-4 w-4" />
                  底圖編輯
                </Link>
              ) : (
                <p className="text-xs text-text-tertiary">您目前的角色無法編輯底圖。</p>
              )}
            </div>
          ) : floor.status === "loaded" && floor.version ? (
            <FloorCanvas
              ref={svgRef}
              version={floor.version}
              markers={floor.markers}
              tenantParam={tenantParam}
              floorEditorHref={floorEditorHref}
              selectedMarker={selectedMarker}
              onSelectMarker={setSelectedMarker}
              onCanvasClick={onCanvasClick}
              onMarkerDrag={handleMarkerDrag}
              onMarkerArchive={handleMarkerArchive}
              canWrite={canWrite}
              onPublish={handlePublish}
              busyKind={busy}
            />
          ) : null}
        </section>
      </div>

      {/* Add marker modal */}
      {addMarker && (
        <AddMarkerModal
          x={addMarker.x}
          y={addMarker.y}
          onCancel={() => setAddMarker(null)}
          onConfirm={handleAddMarker}
          busy={busy === "create_marker"}
        />
      )}

      {/* Bootstrap building / floor */}
      {bootstrap?.kind === "building" && (
        <NameModal
          title="新增建築"
          placeholder="例：本棟、B 棟"
          onCancel={() => setBootstrap(null)}
          onConfirm={(name) => void handleCreateBuilding(name)}
          busy={busy === "create_building"}
        />
      )}
      {bootstrap?.kind === "floor" && (
        <FloorModal
          onCancel={() => setBootstrap(null)}
          onConfirm={(input) => void handleCreateFloor(input)}
          busy={busy === "create_floor"}
        />
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────

interface FloorCanvasProps {
  version: MapLayoutVersionDto;
  markers: MapMarkerDto[];
  tenantParam: string | null;
  selectedMarker: string | null;
  onSelectMarker: (id: string | null) => void;
  onCanvasClick: (e: React.MouseEvent<SVGSVGElement>) => void;
  onMarkerDrag: (id: string, x: number, y: number) => void | Promise<void>;
  onMarkerArchive: (id: string) => void | Promise<void>;
  canWrite: boolean;
  onPublish: () => void;
  busyKind: string | null;
  /** Href to the dedicated FloorLayoutEditor (where base-map upload + AI parse live). */
  floorEditorHref: string | null;
}

const FloorCanvas = React.forwardRef<SVGSVGElement, FloorCanvasProps>(function FloorCanvas(
  { version, markers, tenantParam, selectedMarker, onSelectMarker, onCanvasClick, onMarkerDrag, onMarkerArchive, canWrite, onPublish, busyKind, floorEditorHref },
  ref,
) {
  const isDraft = version.status === "draft";
  // Parse the AI-produced normalized_json so we can render the
  // walls / rooms / doors / corridors as the canvas backdrop.
  // Operators have explicitly asked to never see the raw R2 source
  // image any more, so layoutSourceUrl is gone from this canvas.
  const layoutGeom = useMemo<NormalizedLayoutDto | null>(() => {
    if (!version.normalized_json) return null;
    try {
      const parsed = JSON.parse(version.normalized_json) as NormalizedLayoutDto;
      if (!parsed || !parsed.image_size) return null;
      return { ...EMPTY_LAYOUT, ...parsed };
    } catch {
      return null;
    }
  }, [version.normalized_json]);
  const [drag, setDrag] = useState<{ id: string; offsetX: number; offsetY: number } | null>(null);
  const localRef = useRef<SVGSVGElement | null>(null);
  React.useImperativeHandle(ref, () => localRef.current as SVGSVGElement);

  const handleMouseDown = (e: React.MouseEvent<SVGGElement>, m: MapMarkerDto) => {
    if (!canWrite) { onSelectMarker(m.id); return; }
    e.stopPropagation();
    onSelectMarker(m.id);
    const svg = localRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width * VIEWBOX_W;
    const py = (e.clientY - rect.top) / rect.height * VIEWBOX_H;
    setDrag({ id: m.id, offsetX: px - m.position_x, offsetY: py - m.position_y });
  };
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!drag) return;
    const svg = localRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width * VIEWBOX_W;
    const py = (e.clientY - rect.top) / rect.height * VIEWBOX_H;
    const m = markers.find((x) => x.id === drag.id);
    if (m) {
      m.position_x = Math.max(0, Math.min(VIEWBOX_W, px - drag.offsetX));
      m.position_y = Math.max(0, Math.min(VIEWBOX_H, py - drag.offsetY));
    }
  };
  const handleMouseUp = () => {
    if (drag) {
      const m = markers.find((x) => x.id === drag.id);
      if (m) void onMarkerDrag(drag.id, m.position_x, m.position_y);
      setDrag(null);
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-text-secondary">
          <span className="rounded bg-bg-raised px-2 py-0.5">v{version.version_no}</span>
          <span className={`rounded px-2 py-0.5 ${isDraft ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>
            {isDraft ? "Draft" : "Published"}
          </span>
          <span>SHA-256: <span className="font-mono">{version.source_sha256?.slice(0, 12) ?? "—"}</span></span>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <>
              {floorEditorHref && (
                <Link
                  to={floorEditorHref}
                  className="btn-secondary inline-flex items-center gap-1.5"
                  title="底圖上傳、AI 解析（牆 / 房 / 門）與幾何編輯都在「底圖編輯」頁完成"
                >
                  <FilePlus2 className="h-3.5 w-3.5" />
                  底圖編輯
                </Link>
              )}
              {isDraft && (
                <button onClick={onPublish} className="btn-primary inline-flex items-center gap-1.5" disabled={busyKind === "publish"}>
                  <Save className="h-3.5 w-3.5" />
                  {busyKind === "publish" ? "發佈中…" : "發佈版本"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <div className="overflow-hidden rounded border border-border bg-bg-base">
        <svg
          ref={localRef}
          viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
          preserveAspectRatio="xMidYMid meet"
          className="w-full select-none"
          onClick={onCanvasClick}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          data-testid="layout-canvas"
        >
          {layoutGeom && (
            // Nested SVG rescales the layout's native pixel space (e.g.
            // 1024x768 from the source plan) into the marker canvas
            // viewBox (1000x700) without us having to scale every
            // coordinate by hand. Markers stay in 1000x700 space.
            <svg
              viewBox={`0 0 ${layoutGeom.image_size.width} ${layoutGeom.image_size.height}`}
              x={0}
              y={0}
              width={VIEWBOX_W}
              height={VIEWBOX_H}
              preserveAspectRatio="xMidYMid meet"
              style={{ overflow: "visible" }}
            >
              <LayoutGeometry layout={layoutGeom} opacity={0.9} />
            </svg>
          )}
          {/* Grid for orientation */}
          <g stroke="rgba(255,255,255,0.04)" strokeWidth={1}>
            {Array.from({ length: 11 }, (_, i) => (
              <line key={`v${i}`} x1={i * 100} y1={0} x2={i * 100} y2={VIEWBOX_H} />
            ))}
            {Array.from({ length: 8 }, (_, i) => (
              <line key={`h${i}`} x1={0} y1={i * 100} x2={VIEWBOX_W} y2={i * 100} />
            ))}
          </g>
          {markers.map((m) => (
            <g
              key={m.id}
              data-marker-id={m.id}
              transform={`translate(${m.position_x}, ${m.position_y})`}
              onMouseDown={(e) => handleMouseDown(e, m)}
              style={{ cursor: canWrite ? "grab" : "default" }}
            >
              <circle
                r={m.id === selectedMarker ? 16 : 12}
                fill={MARKER_FILL[m.marker_type]}
                stroke={m.id === selectedMarker ? "#fff" : "rgba(0,0,0,0.4)"}
                strokeWidth={m.id === selectedMarker ? 3 : 1.5}
              />
              <text
                x={0}
                y={m.id === selectedMarker ? 20 : 16}
                fontSize={9}
                fontWeight={600}
                textAnchor="middle"
                fill="#0b0d10"
                style={{ pointerEvents: "none" }}
              >
                {m.marker_type[0].toUpperCase()}
              </text>
              {m.label && (
                <text x={18} y={4} fontSize={11} fill="rgba(255,255,255,0.85)" style={{ pointerEvents: "none" }}>
                  {m.label}
                </text>
              )}
            </g>
          ))}
        </svg>
      </div>
      {selectedMarker && (
        <div className="mt-3 flex items-center justify-between rounded border border-border bg-bg-surface px-3 py-2 text-sm">
          <span className="text-text-secondary">已選取標記點：<span className="font-mono text-text-primary">{selectedMarker.slice(-8)}</span></span>
          {canWrite && (
            <button onClick={() => onMarkerArchive(selectedMarker)} className="inline-flex items-center gap-1 text-red-400 hover:text-red-300" disabled={busyKind === "archive_marker"}>
              <Trash2 className="h-3.5 w-3.5" /> 刪除
            </button>
          )}
        </div>
      )}
    </div>
  );
});

interface AddMarkerModalProps {
  x: number;
  y: number;
  onCancel: () => void;
  onConfirm: (kind: MarkerType, label: string) => void;
  busy: boolean;
}

function AddMarkerModal({ x, y, onCancel, onConfirm, busy }: AddMarkerModalProps): React.ReactElement {
  const [kind, setKind] = useState<MarkerType>("location");
  const [label, setLabel] = useState("");
  return (
    <Modal title={`在 (${Math.round(x)}, ${Math.round(y)}) 新增 Marker`} onCancel={onCancel}>
      <div className="space-y-3">
        <Field label="類型">
          <select value={kind} onChange={(e) => setKind(e.target.value as MarkerType)} className="input">
            {MARKER_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </Field>
        <Field label="顯示名稱（選填）">
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="例：1F 大廳 NFC tag" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="btn">取消</button>
          <button onClick={() => onConfirm(kind, label)} className="btn-primary" disabled={busy}>
            {busy ? "建立中…" : "新增"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface NameModalProps {
  title: string;
  placeholder: string;
  onCancel: () => void;
  onConfirm: (name: string) => void;
  busy: boolean;
}

function NameModal({ title, placeholder, onCancel, onConfirm, busy }: NameModalProps): React.ReactElement {
  const [name, setName] = useState("");
  return (
    <Modal title={title} onCancel={onCancel}>
      <Field label="名稱">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={placeholder} autoFocus />
      </Field>
      <div className="flex justify-end gap-2 pt-3">
        <button onClick={onCancel} className="btn">取消</button>
        <button onClick={() => name.trim() && onConfirm(name.trim())} className="btn-primary" disabled={busy || !name.trim()}>
          {busy ? "建立中…" : "建立"}
        </button>
      </div>
    </Modal>
  );
}

interface FloorModalProps {
  onCancel: () => void;
  onConfirm: (input: { floor_no: number; floor_name: string }) => void;
  busy: boolean;
}

function FloorModal({ onCancel, onConfirm, busy }: FloorModalProps): React.ReactElement {
  const [floorNo, setFloorNo] = useState("1");
  const [floorName, setFloorName] = useState("1F");
  return (
    <Modal title="新增樓層" onCancel={onCancel}>
      <div className="space-y-3">
        <Field label="樓層編號（可為負數代表 B1）">
          <input type="number" className="input" value={floorNo} onChange={(e) => setFloorNo(e.target.value)} />
        </Field>
        <Field label="顯示名稱">
          <input className="input" value={floorName} onChange={(e) => setFloorName(e.target.value)} placeholder="1F / B1 / RF" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="btn">取消</button>
          <button
            onClick={() => {
              if (!floorName.trim()) return;
              const n = parseInt(floorNo, 10);
              onConfirm({ floor_no: Number.isFinite(n) ? n : 1, floor_name: floorName.trim() });
            }}
            className="btn-primary"
            disabled={busy || !floorName.trim()}
          >
            {busy ? "建立中…" : "建立"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface ModalProps {
  title: string;
  children: React.ReactNode;
  onCancel: () => void;
}

function Modal({ title, children, onCancel }: ModalProps): React.ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-lg border border-border bg-bg-surface p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-3 text-lg font-semibold text-text-primary">{title}</h3>
        {children}
      </div>
    </div>
  );
}
